import { useQuery } from '@tanstack/react-query'
import { useTsrClient } from '../../end-users/tsr'
import { Card, CardContent, CardHeader, CardTitle } from '@alphabait/ui' // card'
import { renderQuestion } from '@/components/charts/question-renderers'
import type { CampaignResponse } from '@/components/charts/question-renderers/types'
import { ChartErrorState } from '@/components/charts/question-renderers/shared/chart-error-state'
import { ChartLoadingState } from '@/components/charts/question-renderers/shared/chart-loading-state'
import { ChartEmptyState } from '@/components/charts/question-renderers/shared/chart-empty-state'
import { useTranslation } from 'react-i18next'

interface CampaignQuestionVisualizerProps {
  campaignId: string
  className?: string
  selectedQuestionIds?: string[] // Optional list of question IDs to filter
  questionContext?: 'all' | 'standard' | 'referral'
}

export function CampaignQuestionVisualizer({
  campaignId,
  className,
  selectedQuestionIds,
  questionContext = 'all',
}: CampaignQuestionVisualizerProps) {
  const tsr = useTsrClient()
  const { t } = useTranslation()
  const {
    data: campaignData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'campaignQuestionsWithAnswers',
      campaignId,
      selectedQuestionIds,
      questionContext,
    ],
    queryFn: async () => {
      const response =
        await tsr.dashboard.getCampaignQuestionsWithAnswers.query({
          params: { campaignId },
          query: {
            aggregatesOnly: false,
            questionIds: selectedQuestionIds?.join(','),
            questionContext,
          },
        })

      if (!response.status || response.status !== 200) {
        throw new Error('Failed to fetch campaign questions with answers')
      }
      return response.body as CampaignResponse
    },
    enabled: !!campaignId,
  })

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="flex justify-center items-center h-64">
          <ChartLoadingState />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="flex flex-col justify-center items-center h-64">
          <ChartErrorState message={t('dashboard.charts.failed')} />
        </CardContent>
      </Card>
    )
  }

  if (!campaignData || campaignData.questions.length === 0) {
    return (
      <Card className={className}>
        <ChartEmptyState message={t('dashboard.charts.no_responses_yet')} />
      </Card>
    )
  }

  return (
    <div className={className}>
      <Card className="border-0 bg-gray-8 shadow-none">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{campaignData.title}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {campaignData.totalParticipants} total participants •{' '}
                {campaignData.questions.length} selected questions
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mx-4">
        {campaignData.questions.map((question) => {
          const renderedQuestion = renderQuestion(
            question,
            campaignData.campaignId,
          )

          if (!renderedQuestion) {
            return (
              <Card key={question.questionId} className="border-0">
                <CardHeader>
                  <CardTitle className="text-sm font-medium">
                    {question.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground">
                    Visualization not available for question type:{' '}
                    {question.type}
                  </div>
                </CardContent>
              </Card>
            )
          }

          return <div key={question.questionId}>{renderedQuestion}</div>
        })}
      </div>
    </div>
  )
}
